/**
 * Browser side of profile pictures: fetching an authenticated picture once per URL, the
 * initials shown while there is none, and the square crop the Settings page uploads.
 *
 * Picture URLs come from the API (`avatar_url`, a path under the API root such as
 * `/users/<id>/avatar?v=<version>`). The image route needs the session, which an `<img src>`
 * cannot send as a bearer token, so pictures are fetched with credentials and shown through
 * `blob:` object URLs (allowed by the app's `img-src 'self' data: blob:` policy). Each URL is
 * fetched at most once per page load; a new upload changes the `v` query and so the URL.
 *
 * @module apps/web/src/lib/avatarImage
 */
import { API_BASE } from './api';

/** Side, in pixels, of the square PNG the Settings page uploads. Matches the server's stored size. */
export const AVATAR_UPLOAD_SIDE = 512;

/** Largest file the Settings page accepts before cropping, in bytes; the server's limit is the same. */
export const AVATAR_MAX_SOURCE_BYTES = 2_000_000;

/** Object-URL promises keyed by picture path, shared by every avatar on the page. */
const cache = new Map<string, Promise<string | null>>();

/**
 * Resolve a picture path to a displayable `blob:` URL, or null when it cannot be shown.
 *
 * Sends cookies and any retained bearer token, accepts only PNG/JPEG/WebP responses, and caches
 * the result (including failures) per path for the life of the page. Never throws.
 */
export function loadAvatarObjectUrl(
  path: string,
  fetcher: typeof fetch = fetch
): Promise<string | null> {
  const cached = cache.get(path);
  if (cached) return cached;
  const token = readSessionToken();
  const pending = fetcher(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(async response => {
      if (
        !response.ok ||
        !/^image\/(png|jpeg|webp)(;|$)/i.test(response.headers.get('Content-Type') || '')
      ) {
        return null;
      }
      return URL.createObjectURL(await response.blob());
    })
    .catch(() => null);
  cache.set(path, pending);
  return pending;
}

/** Forget every cached picture; used by tests and after sign-out. */
export function clearAvatarCache(): void {
  for (const pending of cache.values()) {
    pending.then(url => url && URL.revokeObjectURL(url)).catch(() => {});
  }
  cache.clear();
}

/** Read the retained legacy bearer token, tolerating storage that throws. */
function readSessionToken(): string | null {
  try {
    return localStorage.getItem('flexiplanner-session-token');
  } catch {
    return null;
  }
}

/**
 * Up to two initials for a person: first letters of the first and last words of the name, else
 * the first letter of the email, else `?`. Letters are upper-cased; emoji and other astral
 * characters stay whole.
 */
export function initialsFor(name?: string | null, email?: string | null): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  const first = (word?: string) => (word ? Array.from(word)[0].toUpperCase() : '');
  if (words.length >= 2) return first(words[0]) + first(words[words.length - 1]);
  if (words.length === 1) return first(words[0]);
  return first((email || '').trim()) || '?';
}

/**
 * Center-crop an image file to a square and scale it to `side` pixels as a PNG blob.
 *
 * A thin binding to `createImageBitmap` and canvas; the server still validates, re-encodes and
 * size-limits the result. Throws when the browser cannot decode the file.
 */
export async function squareAvatarBlob(file: Blob, side = AVATAR_UPLOAD_SIDE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const crop = Math.min(bitmap.width, bitmap.height);
    const sx = Math.floor((bitmap.width - crop) / 2);
    const sy = Math.floor((bitmap.height - crop) / 2);
    const target = Math.min(side, crop);
    const canvas = document.createElement('canvas');
    canvas.width = target;
    canvas.height = target;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser cannot prepare images.');
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, sx, sy, crop, crop, 0, 0, target, target);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('Could not prepare the picture.'))),
        'image/png'
      )
    );
  } finally {
    bitmap.close();
  }
}
